import {
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import { getFulfillmentIssues } from "./fulfillmentContext";

const hasText = (value) => !!`${value || ""}`.trim();

const getPhoneDigits = (phone) => `${phone || ""}`.replace(/\D/g, "");

const isVirginKuwaitPhone = (phone) => {
  const value = `${phone || ""}`.trim();
  const digits = getPhoneDigits(phone);
  return (
    value.startsWith("+965") &&
    digits.startsWith("96541") &&
    digits.slice(3).length === 8
  );
};

const isEgyptianMobilePhone = (phone) => {
  const digits = getPhoneDigits(phone);
  if (!digits.startsWith("20")) return false;
  const nationalNumber = digits.slice(2);
  if (nationalNumber.startsWith("0"))
    return /^(010|011|012|015)\d{8}$/.test(nationalNumber);
  return /^(10|11|12|15)\d{8}$/.test(nationalNumber);
};

export const isValidCheckoutEmail = (email) =>
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    `${email || ""}`.trim().toLowerCase(),
  );

export const isValidCheckoutPhone = (phone) => {
  const value = `${phone || ""}`.trim();
  if (!value) return false;
  if (getPhoneDigits(value).startsWith("20"))
    return isEgyptianMobilePhone(value);
  try {
    return isValidPhoneNumber(value) || isVirginKuwaitPhone(value);
  } catch (error) {
    return false;
  }
};

const phoneMatchesSelectedCountry = (phone, address, countries = []) => {
  if (!address?.country) return false;
  const selectedCountry = countries.find(
    (country) => `${country.id}` === `${address.country}`,
  );
  if (!selectedCountry?.dial_code) return false;
  try {
    const parsed = parsePhoneNumber(`${phone || ""}`.trim());
    return (
      !!parsed?.country &&
      parsed.country.toLowerCase() ===
        `${selectedCountry.dial_code}`.trim().toLowerCase()
    );
  } catch (error) {
    return false;
  }
};

export const getCheckoutContactIssues = (
  contact = {},
  settings = {},
  requiresVerification = false,
  { address = {}, countries = [] } = {},
) => {
  const issues = [];
  const name = `${contact.name || ""}`.trim();
  if (requiresVerification) issues.push("contact_verification_required");
  if (!name) issues.push("contact_name_required");
  else if (name.length > 25) issues.push("contact_name_too_long");
  if (!isValidCheckoutPhone(contact.phone)) issues.push("contact_phone_invalid");
  if (settings.email_required && !isValidCheckoutEmail(contact.email))
    issues.push("contact_email_invalid");
  if (settings.ask_for_civil_id && !hasText(contact.civilID))
    issues.push("contact_civil_id_required");
  if (
    settings.force_phone_code_match_country &&
    settings.enable_gcc &&
    address?.country &&
    !phoneMatchesSelectedCountry(contact.phone, address, countries)
  )
    issues.push("contact_phone_country_mismatch");
  return Array.from(new Set(issues));
};

export const isCheckoutContactComplete = (
  contact = {},
  settings = {},
  requiresVerification = false,
  options = {},
) =>
  getCheckoutContactIssues(
    contact,
    settings,
    requiresVerification,
    options,
  ).length === 0;

export const deriveCheckoutV2Step = ({
  contact = {},
  settings = {},
  context,
  cart = {},
  requiresVerification = false,
  address = {},
  countries = [],
} = {}) => {
  const tableContactCanBeSkipped =
    settings.enable_table_qr && !settings.require_dine_in_contact_info;
  if (
    !tableContactCanBeSkipped &&
    !isCheckoutContactComplete(contact, settings, requiresVerification, {
      address,
      countries,
    })
  )
    return "contact";

  const selectionIssues = getFulfillmentIssues(context).filter((issue) =>
    [
      "fulfillment_type",
      "country",
      "area",
      "branch",
      "branch_delivery_charge",
    ].includes(issue),
  );
  const needsPhysicalContext =
    Number(cart.delivered_items || 0) > 0 ||
    Number(cart.charged_items || 0) > 0;
  if (needsPhysicalContext && selectionIssues.length) return "fulfillment";

  if (needsPhysicalContext && context?.type === "delivery") {
    const hasCoreAddress =
      hasText(context.address.block) &&
      hasText(context.address.street) &&
      hasText(context.address.building);
    if (!hasCoreAddress) return "address";
  }

  return "review";
};
